import React, { useState } from 'react';
import axios from 'axios';
import { getToken } from '../../../services/auth';

interface Props {
  patientId: string;
  onClose: () => void;
}

const SEXTANTS = [
  { id: 's1', label: 'UR (17-14)', teeth: '17-14' },
  { id: 's2', label: 'UA (13-23)', teeth: '13-23' },
  { id: 's3', label: 'UL (24-27)', teeth: '24-27' },
  { id: 's4', label: 'LL (37-34)', teeth: '37-34' },
  { id: 's5', label: 'LA (33-43)', teeth: '33-43' },
  { id: 's6', label: 'LR (44-47)', teeth: '44-47' },
];

const BPE_CODES = [
  { value: 0, label: '0', desc: 'Healthy', color: '#059669' },
  { value: 1, label: '1', desc: 'Bleeding on probing', color: '#84cc16' },
  { value: 2, label: '2', desc: 'Calculus/overhangs', color: '#f59e0b' },
  { value: 3, label: '3', desc: 'Pocket 3.5-5.5mm', color: '#f97316' },
  { value: 4, label: '4', desc: 'Pocket >5.5mm', color: '#ef4444' },
  { value: 5, label: '*', desc: 'Furcation involvement', color: '#7c3aed' },
];

export default function PerioChart({ patientId, onClose }: Props) {
  const [scores, setScores] = useState<Record<string, number>>({ s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      await axios.post('/api/perio/bpe', { patient_id: patientId, ...scores, notes }, { headers });
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose(); }, 1500);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const maxScore = Math.max(...Object.values(scores));
  const getColor = (v: number) => BPE_CODES.find((c) => c.value === v)?.color || '#94a3b8';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 24, width: 560,
        maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', color: '#1e293b' }}>🦷 Periodontal BPE Chart</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>Basic Periodontal Examination — click cells to set scores</p>

        {/* Sextant Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {SEXTANTS.map((sx) => (
            <div key={sx.id} style={{
              border: `2px solid ${getColor(scores[sx.id])}`,
              borderRadius: 12, padding: 12, textAlign: 'center',
              background: `${getColor(scores[sx.id])}10`,
              transition: 'all 0.3s',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{sx.label}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8 }}>{sx.teeth}</div>
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                {BPE_CODES.map((code) => (
                  <button key={code.value} onClick={() => setScores({ ...scores, [sx.id]: code.value })}
                    title={code.desc}
                    style={{
                      width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: 12, transition: 'all 0.2s',
                      background: scores[sx.id] === code.value ? code.color : '#f1f5f9',
                      color: scores[sx.id] === code.value ? '#fff' : '#64748b',
                      transform: scores[sx.id] === code.value ? 'scale(1.15)' : 'scale(1)',
                    }}>
                    {code.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div style={{
          background: `${getColor(maxScore)}10`, borderRadius: 10, padding: 12, marginBottom: 16,
          border: `1px solid ${getColor(maxScore)}30`, textAlign: 'center',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
            Highest Score: <span style={{ color: getColor(maxScore), fontSize: 18 }}>{maxScore === 5 ? '*' : maxScore}</span>
            {' '} — {BPE_CODES.find((c) => c.value === maxScore)?.desc}
          </span>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {BPE_CODES.map((c) => (
            <span key={c.value} style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: 4,
              background: `${c.color}15`, color: c.color, border: `1px solid ${c.color}30`,
            }}>
              <strong>{c.label}</strong> {c.desc}
            </span>
          ))}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#334155', display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600, cursor: 'pointer',
            background: saved ? '#059669' : 'linear-gradient(135deg, #8b5cf6, #3b82f6)', color: '#fff',
          }}>{saving ? 'Saving...' : saved ? '✓ Saved' : 'Save BPE Record'}</button>
        </div>
      </div>
    </div>
  );
}
